import { ApolloContextValue, ApolloQueryResult } from "@apollo/client";
import { fetchAuthSession, signIn, signOut } from "aws-amplify/auth";
import {
  GetUserDocument,
  GetUserQuery,
  ItemsPurchasableByChamberEnum,
  UserType,
} from "../../../graphql/types";
import { getSingleDashboardUserType } from "./dashboardUserType";
import { getPreserveExpectedErrorsForCallerContext } from "../../../utils/apolloErrorPolicy";

// GetUser during sign in shows its error on the Sign in page, not /500
// (audit 3.1 item 4). Unexpected codes and network failures still go to Sentry.
const signInGetUserErrorContext = getPreserveExpectedErrorsForCallerContext({
  expectedGraphQLErrorCodes: ["FORBIDDEN", "UNAUTHENTICATED"],
});

export const signInHandler = async (
  email: string,
  password: string,
  apolloClient: ApolloContextValue,
  onNewPasswordRequired?: (email: string) => void
): Promise<void> => {
  const user = await signIn({ username: email, password });

  // Cognito does not issue tokens until this challenge is completed. Checking
  // it first preserves Amplify's in-memory continuation for NewPassword.
  if (
    user.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
  ) {
    if (!onNewPasswordRequired) {
      await signOut();
      const challengeError = new Error(
        "A new password must be set interactively."
      );
      challengeError.name = "NewPasswordChallengeRequiresInteractiveLogin";
      throw challengeError;
    }

    onNewPasswordRequired(email);
    return;
  }

  await completeDashboardSignIn(apolloClient);
};

// Use the same access check after ordinary sign-in and password setup.
export const completeDashboardSignIn = async (
  apolloClient: ApolloContextValue
): Promise<void> => {
  const authSession = await fetchAuthSession();
  if (
    authSession.tokens?.idToken?.payload?.["custom:isDisabled"] === "true" ||
    authSession.tokens?.accessToken?.payload?.["custom:isDisabled"] === "true"
  ) {
    await signOut();
    const disabledError = new Error("This account is disabled.");
    disabledError.name = "UserDisabled";
    throw disabledError;
  }

  const signedInUserType = getSingleDashboardUserType(
    authSession.tokens?.accessToken?.payload as Record<string, unknown>
  );
  if (
    signedInUserType !== UserType.ChamberAdmin &&
    signedInUserType !== UserType.ChamberStaff
  ) {
    await signOut();
    const accessError = new Error(
      "This account cannot access this dashboard."
    );
    accessError.name = signedInUserType === UserType.ChamberMemberAdmin
      ? "MemberDashboardRequired"
      : "UserDisabled";
    throw accessError;
  }

  if (!authSession.tokens?.accessToken || !authSession.userSub || !apolloClient.client) {
    await signOut();
    const sessionError = new Error("The sign-in session is incomplete. Sign in again.");
    sessionError.name = "IncompleteDashboardSession";
    throw sessionError;
  }

  const customAccessTokenToCheckIfLoggingInUserHasAccessToChamberMemberPro =
    authSession.tokens.accessToken.toString();
  let userData: ApolloQueryResult<GetUserQuery>;
  try {
    userData = await apolloClient.client.query<GetUserQuery>({
      query: GetUserDocument,
      variables: { userId: authSession.userSub },
      fetchPolicy: "network-only",
      context: {
        ...signInGetUserErrorContext,
        customAccessTokenToCheckIfLoggingInUserHasAccessToChamberMemberPro,
      },
    });
  } catch (error) {
    // Do not leave a half-finished sign in behind; the user signs in again.
    await signOut();
    throw error;
  }

  if (
    !(userData?.data?.getUser?.chamberInfo?.purchasedItems || []).includes(
      ItemsPurchasableByChamberEnum.ChamberMemberPro
    )
  ) {
    await signOut();
    const accessError = new Error(
      "This chamber does not have access to Chamber Member Pro."
    );
    accessError.name = "UserDoesNotHaveAccessToChamberMemberPro";
    throw accessError;
  }
  window.location.href = "/";
};
